import { Icon } from "@mg/dali/src";
import {
  CaretRight,
  ChatCircle,
  Gear,
  House,
  Kanban,
  Minus,
  SidebarSimple,
  Trash,
  UserCircle,
} from "@phosphor-icons/react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DropdownMenu,
  Flex,
  IconButton,
  Link,
  Text,
} from "@radix-ui/themes";
import {
  Link as NavLink,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import cx from "classnames";
import { usePostHog } from "posthog-js/react";
import {
  createContext,
  type ComponentProps,
  type ReactNode,
  useContext,
  useState,
} from "react";

import { FolderTreeView } from "./AppDrawer";
import { AvatarWithInitials } from "./AvatarWithInitials";
import { EditProfileDialog } from "./EditProfileDialog";
import EnterpriseSwitcher from "./EnterpriseSwitcher";
import { useAutoCollapseSidebar } from "./hooks/useAutoCollapseSidebar";
import { useIsOnAskPuntt } from "./hooks/useIsOnAskPuntt";
import { Sidebar } from "./sidebar/Sidebar";
import { SidebarContent } from "./sidebar/SidebarContent";
import { SidebarFooter } from "./sidebar/SidebarFooter";
import { SidebarGroup } from "./sidebar/SidebarGroup";
import { SidebarHeader } from "./sidebar/SidebarHeader";
import { SidebarMenu } from "./sidebar/SidebarMenu";
import { SidebarMenuButton } from "./sidebar/SidebarMenuButton";
import { SidebarMenuItem } from "./sidebar/SidebarMenuItem";
import { SidebarProvider } from "./sidebar/SidebarProvider";
import { SidebarRail } from "./sidebar/SidebarRail";
import { useSidebar } from "./sidebar/useSidebar";

import { ReactComponent as Logo } from "../images/Logo-blue.svg";
import { ReactComponent as Wordmark } from "../images/Logotype.svg";
import { campaignRoute } from "../routes/ask-puntt/campaignRoute";
import { askPunttCampaignsRoute } from "../routes/ask-puntt/campaignsRoute";
import { askPunttChatRoute } from "../routes/ask-puntt/chatRoute";
import { askPunttChatsRoute } from "../routes/ask-puntt/chatsRoute";
import { askPunttRoute } from "../routes/ask-puntt/route";
import { useStartNewProjectChat } from "../routes/ask-puntt/useStartNewProjectChat";
import { indexRoute } from "../routes/index/route";
import { roiRoute } from "../routes/roi/route";
import { archivedUsersRoute } from "../routes/settings/routes/archivedUsersRoute";
import { integrationSettingsRoute } from "../routes/settings/routes/integrationSettingsRoute";
import { knowledgeDatabaseRoute } from "../routes/settings/routes/knowledge-database/route";
import { knowledgeSettingRoute } from "../routes/settings/routes/knowledgeSettingRoute";
import { usersRoute } from "../routes/settings/routes/usersRoute";
import { ticketsRoute } from "../routes/tickets/route";
import { useAnalytics } from "../utils/analytics";
import {
  canAccessAskPuntt,
  canManageBrandGuidelines,
  canAccessKnowledgeDatabase,
  canManageUsers,
  canAccessPunttDashboard,
  isDemoEnterprise,
  isMeaningfulGigsRole,
  showDocumentsInSettings,
  showDocumentsInSidebar,
} from "../utils/auth";
import { getNameInitials } from "../utils/constants";
import { isNil } from "../utils/fp";
import { useAppDispatch, useAppSelector } from "../utils/hooks";
import { assetForUser } from "../utils/imageHandler";
import {
  useAskPunttChats,
  useDeleteAskPunttChat,
} from "../utils/queries/askPuntt";
import { useAskPunttProjects } from "../utils/queries/askPunttProjects";
import { logout } from "../utils/slices/auth";
import { setDrawerOpen } from "../utils/slices/ui";

const navLinkClasses =
  "transition-colors [&_button]:transition-colors [&_button]:hover:bg-puntt-neutral-grayA-2";
const activeNavLinkClasses =
  "[&_button]:!bg-puntt-neutral-grayA-3 [&_button]:hover:!bg-puntt-neutral-grayA-4 [&_button]:active:!bg-puntt-neutral-grayA-5";

// Shared explicit footprint for the three footer trigger cards (settings,
// login, account). They MUST share the same outer dimensions: in the
// collapsed icon rail (`calc(3.5rem + 1px)` = 57px) the SidebarFooter `p-4`
// leaves a 25px-wide button area, so any card wider than 24px (e.g. the
// previous `size-7` = 28px) overflows and is visibly clipped on the right
// by `SidebarMenuButton`'s `overflow-hidden`. Keeping all three cards at
// `size-6` (24px) matches the Radix Avatar `size="1"` footprint, fits
// cleanly in both collapsed and expanded rail widths, and gives the three
// cards a consistent vertical rhythm.
export const SIDEBAR_TRIGGER_CARD_SIZE_CLASS = "size-6";

// useAutoCollapseSidebar + the FOCUS_PATH_FRAGMENTS list live in
// hooks/useAutoCollapseSidebar.ts so the category-transition logic is
// testable in isolation. AppSidebar consumes the hook directly.

export function AppSidebar() {
  const posthog = usePostHog();
  const analytics = useAnalytics("AppSidebar");
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);

  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.value)!;
  const sidebarOpen = useAppSelector((state) => state.ui.drawerOpen.app);

  // Demo-aware auto-collapse — every tenant benefits from the focus
  // surface auto-collapse, not just demo. Real customers' chat / ticket
  // detail pages are also dense enough to want the rail.
  useAutoCollapseSidebar();

  return (
    <SidebarProvider
      name="app"
      open={sidebarOpen}
      onOpenChange={(open) => {
        analytics.capture(`sidebar_${open ? "opened" : "closed"}`);
        dispatch(setDrawerOpen({ app: open }));
      }}
    >
      <Sidebar data-testid="app-sidebar">
        <AppSidebarHeader />

        <SidebarContent data-testid="app-sidebar-content">
          <Flex direction="column" className="min-h-0 flex-1 overflow-hidden">
            <SidebarGroup className="shrink-0 pb-0">
              <SidebarMenu>
                <SidebarMenuItem
                  data-testid="sidebar-menu-item-roi-dashboard"
                  data-auth-trigger="roi-dashboard"
                  hidden={!isMeaningfulGigsRole()}
                >
                  <Link size="3" weight="medium" className="puntt-link" asChild>
                    <NavLink
                      to={roiRoute.to}
                      className={navLinkClasses}
                      activeProps={{
                        className: activeNavLinkClasses,
                      }}
                    >
                      <SidebarMenuButton
                        tooltip={
                          <Flex align="center" className="gap-3">
                            <House
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                            <Text
                              size="3"
                              className="puntt-link translate-y-1/2 opacity-0 duration-150 group-hover/menu-item-tooltip:translate-y-0 group-hover/menu-item-tooltip:opacity-100"
                            >
                              ROI Dashboard
                            </Text>
                          </Flex>
                        }
                      >
                        <Box className="shrink-0 p-1">
                          <House size={18} color="rgb(var(--puntt-blue-12))" />
                        </Box>
                        ROI Dashboard
                      </SidebarMenuButton>
                    </NavLink>
                  </Link>
                </SidebarMenuItem>

                <SidebarMenuItem
                  data-testid="sidebar-menu-item-dashboard"
                  data-auth-trigger="dashboard"
                  hidden={!canAccessPunttDashboard()}
                >
                  <Link size="3" weight="medium" className="puntt-link" asChild>
                    <NavLink
                      to={indexRoute.to}
                      className={navLinkClasses}
                      activeProps={{
                        className: activeNavLinkClasses,
                      }}
                    >
                      <SidebarMenuButton
                        tooltip={
                          <Flex align="center" className="gap-3">
                            <House
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                            <Text
                              size="3"
                              className="puntt-link translate-y-1/2 opacity-0 duration-150 group-hover/menu-item-tooltip:translate-y-0 group-hover/menu-item-tooltip:opacity-100"
                            >
                              Home
                            </Text>
                          </Flex>
                        }
                      >
                        <Box className="shrink-0 p-1">
                          <House size={18} color="rgb(var(--puntt-blue-12))" />
                        </Box>
                        Home
                      </SidebarMenuButton>
                    </NavLink>
                  </Link>
                </SidebarMenuItem>

                <SidebarMenuItem
                  data-testid="sidebar-menu-item-ask-puntt"
                  data-auth-trigger="ask-puntt"
                  hidden={!canAccessAskPuntt()}
                >
                  <Link size="3" weight="medium" className="puntt-link" asChild>
                    <NavLink
                      to={askPunttRoute.to}
                      className={navLinkClasses}
                      activeProps={{
                        className: activeNavLinkClasses,
                      }}
                    >
                      <SidebarMenuButton
                        tooltip={
                          <Flex align="center" className="gap-3">
                            <ChatCircle
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                            <Text
                              size="3"
                              className="puntt-link translate-y-1/2 opacity-0 duration-150 group-hover/menu-item-tooltip:translate-y-0 group-hover/menu-item-tooltip:opacity-100"
                            >
                              Ask Puntt
                            </Text>
                          </Flex>
                        }
                      >
                        <Box className="shrink-0 p-1">
                          <ChatCircle
                            size={18}
                            color="rgb(var(--puntt-blue-12))"
                          />
                        </Box>
                        Ask Puntt
                      </SidebarMenuButton>
                    </NavLink>
                  </Link>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroup>

            {/*
              Ask Puntt projects/chats lists. When Documents is hidden the
              slot expands to fill the rail and pins "View all" links; when
              Documents is visible the slot stays content-sized so Documents
              sits directly underneath.
            */}
            <AskPunttSidebarScrollSlot>
              <AskPunttProjects />
              <AskPunttSharedWithMe />
              <AskPunttRecents />
            </AskPunttSidebarScrollSlot>

            {showDocumentsInSidebar() && (
              <SidebarGroup className="shrink-0 pb-0">
                <SidebarMenu>
                  <SidebarMenuItem
                    data-testid="sidebar-menu-item-projects"
                    data-auth-trigger="projects"
                    hidden={canAccessPunttDashboard()}
                  >
                    <Link
                      size="3"
                      weight="medium"
                      className="puntt-link"
                      asChild
                    >
                      <NavLink
                        to={ticketsRoute.to}
                        className={navLinkClasses}
                        activeProps={{
                          className: activeNavLinkClasses,
                        }}
                      >
                        <SidebarMenuButton
                          tooltip={
                            <Flex align="center" className="gap-3">
                              <Kanban
                                size={18}
                                color="rgb(var(--puntt-blue-12))"
                              />
                              <Text
                                size="3"
                                className="puntt-link translate-y-1/2 opacity-0 duration-150 group-hover/menu-item-tooltip:translate-y-0 group-hover/menu-item-tooltip:opacity-100"
                              >
                                Documents
                              </Text>
                            </Flex>
                          }
                        >
                          <Box className="shrink-0 p-1">
                            <Kanban
                              size={18}
                              color="rgb(var(--puntt-blue-12))"
                            />
                          </Box>
                          Documents
                        </SidebarMenuButton>
                      </NavLink>
                    </Link>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroup>
            )}

            {showDocumentsInSidebar() && <FolderTreeView />}
          </Flex>
        </SidebarContent>

        <SidebarFooter data-testid="app-sidebar-footer">
          <SidebarMenu>
            <SidebarCollapseButton />

            <SidebarMenuItem
              hidden={
                !canAccessKnowledgeDatabase() &&
                !canManageBrandGuidelines() &&
                !canManageUsers()
              }
            >
              <DropdownMenu.Root
                open={isSettingsOpen}
                onOpenChange={setIsSettingsOpen}
              >
                <DropdownMenu.Trigger
                  className="group/dropdown-menu"
                  data-testid="sidebar-settings-trigger"
                >
                  <SidebarMenuButton className="group/menu-button !p-0 group-data-[collapsible=icon]:justify-center">
                    <span
                      aria-hidden
                      data-testid="sidebar-settings-trigger-card"
                      className="flex size-6 shrink-0 items-center justify-center rounded-md border border-puntt-neutral-gray-6 bg-base-white shadow-sm transition-all group-hover/menu-button:border-puntt-neutral-gray-6 group-hover/menu-button:bg-puntt-neutral-gray-3 group-hover/menu-button:shadow-md group-data-[state=open]/dropdown-menu:border-puntt-neutral-gray-6 group-data-[state=open]/dropdown-menu:bg-puntt-neutral-gray-4"
                    >
                      <Gear size={14} className="text-puntt-neutral-gray-11" />
                    </span>

                    <Flex
                      width="100%"
                      className="rounded-md px-1.5 py-1 transition-colors group-hover/menu-button:bg-puntt-neutral-gray-3 group-data-[collapsible=icon]:hidden group-data-[state=open]/dropdown-menu:bg-puntt-neutral-gray-4"
                    >
                      <Text
                        color="gray"
                        size="1"
                        weight="medium"
                        className="flex-1 text-left"
                      >
                        Settings
                      </Text>

                      <CaretRight />
                    </Flex>
                  </SidebarMenuButton>
                </DropdownMenu.Trigger>

                <DropdownMenu.Content side="right">
                  <DropdownMenu.Item
                    hidden={!showDocumentsInSettings()}
                    data-testid="sidebar-settings-documents"
                  >
                    <NavLink
                      data-testid="app-sidebar-nav-link-documents"
                      to={ticketsRoute.to}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Documents
                    </NavLink>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    hidden={!canManageBrandGuidelines()}
                    data-testid="sidebar-settings-knowledge"
                  >
                    <NavLink
                      data-testid="app-sidebar-nav-link-416"
                      to={knowledgeSettingRoute.to}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Knowledge Settings
                    </NavLink>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item hidden={!canAccessKnowledgeDatabase()}>
                    <NavLink
                      data-testid="app-sidebar-nav-link-425"
                      to={knowledgeDatabaseRoute.to}
                      search={{ pageSize: 100 }}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Knowledge Database
                    </NavLink>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    hidden={!canManageUsers()}
                    data-testid="sidebar-settings-integration"
                  >
                    <NavLink
                      data-testid="app-sidebar-nav-link-438"
                      to={integrationSettingsRoute.to}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Integration Settings
                    </NavLink>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    hidden={!canManageUsers()}
                    data-testid="sidebar-settings-manage-users"
                  >
                    <NavLink
                      data-testid="app-sidebar-nav-link-450"
                      to={usersRoute.to}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Manage Users
                    </NavLink>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item hidden={!canManageUsers()}>
                    <NavLink
                      data-testid="app-sidebar-nav-link-459"
                      to={archivedUsersRoute.to}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Archived Users
                    </NavLink>
                  </DropdownMenu.Item>
                  {/*
                    Demo Settings shortcut — only rendered for users
                    sitting on the resettable demo tenant (isDemo=true on
                    the enterprise). All the demo cards (Reset, Brand
                    config, Impersonation, Killer-flow review functions,
                    Pre-run assets) already live inline on /settings; this
                    dropdown entry just lets a demoer jump straight to
                    them via the `#demo-settings` anchor instead of
                    scrolling past Knowledge + Integration cards. Hidden
                    entirely for real customer tenants.
                  */}
                  <DropdownMenu.Item
                    hidden={!isDemoEnterprise()}
                    data-testid="sidebar-settings-demo"
                  >
                    {/*
                      `to` is the literal string `/settings`, not
                      `settingsRoute.to`. Importing the settings route
                      module here would transitively pull in every demo
                      card + auth-layout + the eager `new QueryClient()`
                      at `src/utils/queryClient.tsx`, which crashes
                      `AppSidebar.test.tsx` at import time (its
                      `vi.mock("@tanstack/react-query")` doesn't export
                      `QueryClient`). The string form has the same
                      runtime behaviour for NavLink without the import
                      bloat.
                    */}
                    <NavLink
                      data-testid="app-sidebar-nav-link-demo-settings"
                      to="/settings"
                      hash="demo-settings"
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      Demo Settings
                    </NavLink>
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </SidebarMenuItem>

            <SidebarMenuItem
              hidden={!isNil(user.email)}
              data-auth-trigger="login-signup-button"
            >
              <SidebarMenuButton className="group/menu-button !p-0 group-data-[collapsible=icon]:justify-center">
                <span
                  aria-hidden
                  data-testid="sidebar-login-trigger-card"
                  className="flex size-6 shrink-0 items-center justify-center rounded-md border border-puntt-blue-7 bg-base-white text-puntt-blue-11 shadow-sm transition-all group-hover/menu-button:border-puntt-blue-9 group-hover/menu-button:bg-puntt-blue-2 group-hover/menu-button:shadow-md"
                >
                  <UserCircle size={14} />
                </span>

                <Flex
                  width="100%"
                  className="rounded-md px-1.5 py-1 transition-colors group-hover/menu-button:bg-puntt-neutral-gray-3 group-data-[collapsible=icon]:hidden"
                >
                  <Text
                    color="blue"
                    size="1"
                    weight="medium"
                    className="flex-1 whitespace-nowrap text-left"
                  >
                    Log In or Sign Up
                  </Text>
                </Flex>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem hidden={isNil(user.email)}>
              <DropdownMenu.Root
                open={isAccountOpen}
                onOpenChange={setIsAccountOpen}
              >
                <DropdownMenu.Trigger
                  className="group/dropdown-menu"
                  data-testid="sidebar-account-trigger"
                >
                  <SidebarMenuButton className="group/menu-button !p-0 group-data-[collapsible=icon]:justify-center">
                    <Avatar
                      variant="solid"
                      size="1"
                      radius="full"
                      src={assetForUser(user.avatar)}
                      fallback={
                        getNameInitials(user.name) ?? (
                          // Match the outlined `Icon.User` silhouette used by
                          // AvatarWithInitials on the Manage Users page so
                          // the no-name affordance is visually consistent.
                          <Icon.User size={16} />
                        )
                      }
                      data-testid="sidebar-account-trigger-card"
                      className="size-6 shrink-0 border border-puntt-neutral-gray-6 shadow-sm transition-all group-hover/menu-button:border-puntt-neutral-gray-6 group-hover/menu-button:shadow-md group-data-[state=open]/dropdown-menu:border-puntt-neutral-gray-6"
                    />

                    <Flex
                      width="100%"
                      className="rounded-md px-1.5 py-1 transition-colors group-hover/menu-button:bg-puntt-neutral-gray-3 group-data-[collapsible=icon]:hidden group-data-[state=open]/dropdown-menu:bg-puntt-neutral-gray-4"
                    >
                      <Text
                        color="gray"
                        size="1"
                        weight="medium"
                        className="flex-1 text-left"
                      >
                        Account
                      </Text>

                      <CaretRight />
                    </Flex>
                  </SidebarMenuButton>
                </DropdownMenu.Trigger>

                <DropdownMenu.Content side="right" className="min-w-[220px]">
                  {/* Header area */}
                  <div className="flex flex-col items-center py-4">
                    <AvatarWithInitials
                      size={16}
                      name={user.name}
                      avatar={user.avatar}
                    />
                    <span className="mt-2 text-center text-lg font-semibold">
                      {user.name}
                    </span>
                    <span className="text-gray-500 mt-1 text-center text-sm">
                      {user.email}
                    </span>
                  </div>
                  <div className="border-gray-200 my-2 border-t" />
                  <DropdownMenu.Item
                    onClick={() => {
                      setIsEditProfileOpen(true);
                      setIsAccountOpen(false);
                    }}
                    className="cursor-pointer"
                  >
                    Edit Profile
                  </DropdownMenu.Item>
                  <div className="border-gray-200 my-2 border-t" />
                  <DropdownMenu.Item
                    onClick={() => {
                      dispatch(logout());
                      posthog.reset();
                      setIsAccountOpen(false);
                    }}
                    className="text-red-600 cursor-pointer"
                    data-testid="sidebar-account-logout"
                  >
                    Log Out
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail data-testid="app-sidebar-rail" />
      </Sidebar>

      <EditProfileDialog
        open={isEditProfileOpen}
        onOpenChange={setIsEditProfileOpen}
      />
    </SidebarProvider>
  );
}

/**
 * Always-visible footer affordance for collapsing / expanding the rail.
 *
 * The sidebar can already be toggled via the floating `SidebarTrigger`
 * (hover-only) and the draggable `SidebarRail` (edge-only), but neither
 * reads as an obvious "open / close the nav" control. This button sits
 * above Settings + Account in the footer, mirrors their outlined-card
 * treatment, and labels the action explicitly so the toggle is
 * discoverable. It must live in its own component because `useSidebar`
 * can only be read inside `SidebarProvider`, which `AppSidebar` renders.
 */
function SidebarCollapseButton() {
  const { open, toggleSidebar } = useSidebar();
  const label = open ? "Collapse Sidebar" : "Expand Sidebar";

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        type="button"
        onClick={toggleSidebar}
        aria-label={label}
        data-testid="sidebar-collapse-trigger"
        className="group/menu-button !p-0 group-data-[collapsible=icon]:justify-center"
      >
        <span
          aria-hidden
          data-testid="sidebar-collapse-trigger-card"
          className="flex size-6 shrink-0 items-center justify-center rounded-md border border-puntt-neutral-gray-6 bg-base-white shadow-sm transition-all group-hover/menu-button:border-puntt-neutral-gray-6 group-hover/menu-button:bg-puntt-neutral-gray-3 group-hover/menu-button:shadow-md"
        >
          <SidebarSimple size={14} className="text-puntt-neutral-gray-11" />
        </span>

        <Flex
          width="100%"
          className="rounded-md px-1.5 py-1 transition-colors group-hover/menu-button:bg-puntt-neutral-gray-3 group-data-[collapsible=icon]:hidden"
        >
          <Text
            color="gray"
            size="1"
            weight="medium"
            className="flex-1 text-left"
          >
            {label}
          </Text>
        </Flex>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function AppSidebarHeader() {
  const user = useAppSelector((state) => state.auth.value);
  const { open } = useSidebar();

  return (
    <SidebarHeader
      data-testid="app-sidebar-header"
      data-auth-trigger="sidebar-header"
    >
      <Flex gap="4" justify={open ? "between" : "center"} align="center">
        <NavLink to={indexRoute.to}>
          {open ? (
            <Wordmark className="h-4 max-w-max" />
          ) : (
            <Logo className="size-6" />
          )}
        </NavLink>

        {open && !isNil(user?.email) ? <EnterpriseSwitcher /> : null}
      </Flex>
    </SidebarHeader>
  );
}

type AskPunttSectionLayout = "static" | "compact" | "expand";

const AskPunttSidebarLayoutContext = createContext({ fillsRail: false });

function useAskPunttSidebarLayout() {
  return useContext(AskPunttSidebarLayoutContext);
}

function askPunttSectionShellClass(
  layout: AskPunttSectionLayout,
  fillsRail: boolean,
) {
  if (!fillsRail) {
    return "p-2";
  }
  return cx(
    "flex min-h-0 flex-col overflow-hidden p-2",
    layout === "expand" && "flex-1",
    layout === "compact" && "max-h-[45%] shrink-0",
  );
}

function AskPunttSidebarSection({
  testId,
  layout,
  header,
  footer,
  children,
  ...rest
}: {
  testId: string;
  layout: AskPunttSectionLayout;
  header: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
} & Omit<ComponentProps<"div">, "children">) {
  const { fillsRail } = useAskPunttSidebarLayout();

  return (
    <div
      data-testid={testId}
      className={askPunttSectionShellClass(layout, fillsRail)}
      {...rest}
    >
      <div className="shrink-0">{header}</div>
      <div
        className={cx(fillsRail && "min-h-0 flex-1 overflow-y-auto")}
        data-testid={`${testId}-list`}
      >
        {children}
      </div>
      {footer != null ? <div className="shrink-0">{footer}</div> : null}
    </div>
  );
}

/**
 * Scroll-confine wrapper for the AskPunttProjects + AskPunttRecents lists.
 *
 * Two layout modes on /ask-puntt:
 *
 * 1. **Documents visible** (`showDocumentsInSidebar()`): the slot stays
 *    content-sized (`shrink basis-auto overflow-y-auto`) so Documents +
 *    FolderTreeView sit directly under the chat list. Do NOT add `flex-1`
 *    here — that maroons Documents at the bottom with a dead gap.
 *
 * 2. **Documents hidden** (Ask Puntt enterprise toggle): the slot expands
 *    (`flex-1 overflow-hidden`) to fill the rail. Each section pins its
 *    header + "View all" footer while only the item list scrolls internally.
 *
 * On every other route: collapse to `display: contents` so FolderTreeView
 * keeps flex-1 on /tickets.
 */
function AskPunttSidebarScrollSlot({ children }: { children: ReactNode }) {
  const isOnAskPuntt = useIsOnAskPuntt();
  const fillsRail = isOnAskPuntt && !showDocumentsInSidebar();

  return (
    <AskPunttSidebarLayoutContext.Provider value={{ fillsRail }}>
      <div
        data-testid="sidebar-ask-puntt-scroll-slot"
        data-active={isOnAskPuntt ? "true" : "false"}
        data-fills-rail={fillsRail ? "true" : "false"}
        className={cx(
          isOnAskPuntt
            ? fillsRail
              ? "flex min-h-0 flex-1 flex-col overflow-hidden"
              : "flex min-h-0 shrink basis-auto flex-col overflow-y-auto"
            : "contents",
        )}
      >
        {children}
      </div>
    </AskPunttSidebarLayoutContext.Provider>
  );
}

function hasAskPunttRecentsSection(
  chats: { workflowType?: string | null }[] | undefined,
) {
  return (
    (chats?.filter((chat) => chat.workflowType !== "project").length ?? 0) > 0
  );
}

function AskPunttProjects() {
  const { data } = useAskPunttProjects({ scope: "all" });
  const { data: chatsData } = useAskPunttChats();
  const { data: sharedData } = useAskPunttChats({ scope: "shared" });
  const { open } = useSidebar();
  const { matches } = useRouterState();
  const isOnAskPuntt = useIsOnAskPuntt();
  const { fillsRail } = useAskPunttSidebarLayout();
  const { startNewProjectChat, isPending: isStartingProject } =
    useStartNewProjectChat("ask_puntt_sidebar");

  if (!canAccessAskPuntt() || !open || !isOnAskPuntt) return null;

  const projects = data?.projects ?? [];
  if (projects.length === 0) return null;

  const hasLowerSections =
    hasAskPunttRecentsSection(chatsData?.chats) ||
    (sharedData?.chats?.length ?? 0) > 0;
  const layout: AskPunttSectionLayout =
    fillsRail && hasLowerSections ? "compact" : fillsRail ? "expand" : "static";

  const isActiveProject = (projectId: string) =>
    matches.some((m) => m.pathname === `/ask-puntt/campaigns/${projectId}`);

  return (
    <AskPunttSidebarSection
      testId="sidebar-ask-puntt-projects"
      layout={layout}
      header={
        <>
          <div
            className="relative flex w-full items-center gap-4 rounded-md px-2 py-1.5"
            style={{ background: "inherit" }}
            data-testid="sidebar-ask-puntt-projects-header"
          >
            <Text size="2" weight="bold" className="flex-1 truncate px-0">
              Projects
            </Text>
            <div className="absolute right-3 top-1/2 flex h-full -translate-y-1/2 items-center">
              <Button
                size="1"
                variant="ghost"
                color="gray"
                className="hover:bg-puntt-neutral-gray-4 hover:shadow-sm"
                style={{ zIndex: 2 }}
                disabled={isStartingProject}
                onClick={() =>
                  void startNewProjectChat("sidebar_projects_plus")
                }
                aria-label="New project"
                data-testid="sidebar-ask-puntt-new-project"
              >
                +
              </Button>
            </div>
          </div>
        </>
      }
      footer={
        <Link size="1" className="puntt-link" asChild>
          <NavLink
            to={askPunttCampaignsRoute.to}
            className="mt-1 block px-3 py-1.5 text-puntt-neutral-gray-10 transition-colors hover:text-puntt-accent-9"
            data-testid="sidebar-ask-puntt-projects-all-link"
          >
            <Text size="1">View all projects →</Text>
          </NavLink>
        </Link>
      }
    >
      {projects.slice(0, 8).map((project) => (
        <div
          key={project._id}
          className={cx(
            "group/chat-item relative flex w-full items-center gap-4 rounded-md py-1.5 pl-3 pr-2 transition-colors hover:bg-puntt-neutral-gray-3",
            isActiveProject(project._id) &&
              "bg-puntt-neutral-gray-3 hover:bg-puntt-neutral-gray-4",
          )}
          data-testid="sidebar-ask-puntt-project-item"
          data-project-id={project._id}
        >
          <Link
            asChild
            size="2"
            weight="medium"
            className="puntt-link flex-1 overflow-hidden"
          >
            <NavLink
              to={campaignRoute.to}
              params={{ projectId: project._id }}
              className="flex w-full items-start gap-2 overflow-hidden"
            >
              <span className="flex flex-1 flex-col overflow-hidden">
                <span className="block w-full truncate">{project.name}</span>
                {project.role === "member" ? (
                  <span
                    className="block w-full truncate text-puntt-neutral-gray-10"
                    style={{ fontSize: "11px", lineHeight: "14px" }}
                    data-testid="sidebar-ask-puntt-project-shared"
                  >
                    Shared with you
                  </span>
                ) : null}
              </span>
            </NavLink>
          </Link>
        </div>
      ))}
    </AskPunttSidebarSection>
  );
}

const SIDEBAR_RECENTS_LIMIT = 8;

function AskPunttRecents() {
  const { data } = useAskPunttChats();
  const { open } = useSidebar();
  const { matches } = useRouterState();
  const navigate = useNavigate();
  const deleteChatMutation = useDeleteAskPunttChat();
  const isOnAskPuntt = useIsOnAskPuntt();
  const { fillsRail } = useAskPunttSidebarLayout();
  const [chatIdToDelete, setChatIdToDelete] = useState<string | null>(null);
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);

  if (!canAccessAskPuntt() || !open || !isOnAskPuntt || !data?.chats?.length)
    return null;

  const nonProjectChats =
    data.chats.filter((c) => c.workflowType !== "project") ?? [];
  if (nonProjectChats.length === 0) return null;

  const isActiveChat = (chatId: string) =>
    matches.some((m) => m.pathname === `/ask-puntt/${chatId}`);

  function handleDelete(chatId: string) {
    const currentPath = window.location.pathname;
    const isViewing = currentPath.includes(`/ask-puntt/${chatId}`);
    deleteChatMutation.mutate(chatId, {
      onSuccess: () => {
        setChatIdToDelete(null);
        if (isViewing) {
          navigate({ to: askPunttRoute.to });
        }
      },
    });
  }

  const chatToDelete = nonProjectChats.find((c) => c._id === chatIdToDelete);
  const layout: AskPunttSectionLayout = fillsRail ? "expand" : "static";

  return (
    <>
      <AskPunttSidebarSection
        testId="sidebar-ask-puntt-chats"
        layout={layout}
        header={
          <div
            className="relative flex w-full items-center gap-4 rounded-md px-2 py-1.5"
            style={{ background: "inherit" }}
            data-testid="sidebar-ask-puntt-chats-header"
          >
            <Text size="2" weight="bold" className="flex-1 truncate px-0">
              Chats
            </Text>
            <div className="absolute right-3 top-1/2 flex h-full -translate-y-1/2 items-center">
              <Button
                size="1"
                variant="ghost"
                color="gray"
                className="hover:bg-puntt-neutral-gray-4 hover:shadow-sm"
                style={{ zIndex: 2 }}
                onClick={() => navigate({ to: askPunttRoute.to })}
                aria-label="New Chat"
                data-testid="sidebar-ask-puntt-new-chat"
              >
                +
              </Button>
            </div>
          </div>
        }
        footer={
          nonProjectChats.length > SIDEBAR_RECENTS_LIMIT ? (
            <Link size="1" className="puntt-link" asChild>
              <NavLink
                to={askPunttChatsRoute.to}
                className="mt-1 block px-3 py-1.5 text-puntt-neutral-gray-10 transition-colors hover:text-puntt-accent-9"
                data-testid="sidebar-ask-puntt-chats-all-link"
              >
                <Text size="1">View all chats →</Text>
              </NavLink>
            </Link>
          ) : undefined
        }
      >
        {nonProjectChats.slice(0, SIDEBAR_RECENTS_LIMIT).map((chat) => {
          // Treat as own until auth resolves so the delete button doesn't
          // briefly disappear from own chats before currentUserId loads.
          const isOwnChat =
            currentUserId === undefined || chat.userId === currentUserId;
          return (
            <div
              key={chat._id}
              className={cx(
                "group/chat-item relative flex w-full items-center gap-4 rounded-md py-1.5 pl-3 pr-2 transition-colors hover:bg-puntt-neutral-gray-3",
                isActiveChat(chat._id) &&
                  "bg-puntt-neutral-gray-3 hover:bg-puntt-neutral-gray-4",
              )}
              data-testid="sidebar-ask-puntt-chat-item"
              data-chat-id={chat._id}
            >
              <Link
                asChild
                size="2"
                weight="medium"
                className="puntt-link flex-1 overflow-hidden"
              >
                <NavLink
                  to={askPunttChatRoute.to}
                  params={{ chatId: chat._id }}
                  className="w-full overflow-hidden"
                >
                  <span className="block w-full truncate">{chat.title}</span>
                  {!isOwnChat && chat.ownerName ? (
                    <span
                      className="block w-full truncate text-puntt-neutral-gray-10"
                      style={{ fontSize: "11px", lineHeight: "14px" }}
                      data-testid="sidebar-ask-puntt-chat-owner"
                    >
                      {chat.ownerName}
                    </span>
                  ) : null}
                </NavLink>
              </Link>
              {isOwnChat ? (
                <div className="absolute right-3 top-1/2 flex h-full -translate-y-1/2 items-center">
                  <IconButton
                    size="2"
                    variant="ghost"
                    color="gray"
                    className="hidden hover:bg-puntt-red-9 hover:text-base-white group-hover/chat-item:flex"
                    style={{ zIndex: 2 }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setChatIdToDelete(chat._id);
                    }}
                    aria-label="Delete chat"
                    data-testid="sidebar-ask-puntt-delete-chat"
                  >
                    <Minus size={12} weight="bold" />
                  </IconButton>
                </div>
              ) : null}
            </div>
          );
        })}
      </AskPunttSidebarSection>

      <Dialog.Root
        open={chatIdToDelete != null}
        onOpenChange={(open) => {
          if (!open) setChatIdToDelete(null);
        }}
      >
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>Delete chat?</Dialog.Title>
          <Dialog.Description>
            Are you sure you want to delete{" "}
            {chatToDelete ? <em>{chatToDelete.title}</em> : "this chat"}?
          </Dialog.Description>
          <Flex gap="2" mt="3" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setChatIdToDelete(null)}
              data-testid="sidebar-delete-chat-cancel"
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => chatIdToDelete && handleDelete(chatIdToDelete)}
              disabled={deleteChatMutation.isPending}
              loading={deleteChatMutation.isPending}
              data-testid="sidebar-delete-chat-confirm"
            >
              <Trash />
              Delete
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}

const SIDEBAR_SHARED_LIMIT = 8;

/**
 * "Shared with me" sidebar group — chats where the current user appears in
 * the chat's `allowList` (chat-sharing v1). Hidden until the user has at
 * least one shared chat. Sits between AskPunttProjects and AskPunttRecents
 * so it is visually grouped with the rest of the AP-only navigation.
 */
function AskPunttSharedWithMe() {
  const { data } = useAskPunttChats({ scope: "shared" });
  const { data: chatsData } = useAskPunttChats();
  const { open } = useSidebar();
  const { matches } = useRouterState();
  const isOnAskPuntt = useIsOnAskPuntt();
  const { fillsRail } = useAskPunttSidebarLayout();

  if (!canAccessAskPuntt() || !open || !isOnAskPuntt) return null;

  const sharedChats = data?.chats ?? [];
  if (sharedChats.length === 0) return null;

  const hasRecentsBelow = hasAskPunttRecentsSection(chatsData?.chats);
  const layout: AskPunttSectionLayout =
    fillsRail && hasRecentsBelow ? "compact" : fillsRail ? "expand" : "static";

  const isActiveChat = (chatId: string) =>
    matches.some((m) => m.pathname === `/ask-puntt/${chatId}`);

  return (
    <AskPunttSidebarSection
      testId="sidebar-ask-puntt-shared-section"
      layout={layout}
      aria-label="Chats shared with me"
      role="navigation"
      header={
        <div
          className="relative flex w-full items-center gap-4 rounded-md px-2 py-1.5"
          style={{ background: "inherit" }}
          data-testid="sidebar-ask-puntt-shared-header"
        >
          <Text size="2" weight="bold" className="flex-1 truncate px-0">
            Shared with me
          </Text>
        </div>
      }
      footer={
        sharedChats.length > SIDEBAR_SHARED_LIMIT ? (
          <Link size="1" className="puntt-link" asChild>
            <NavLink
              to={askPunttChatsRoute.to}
              className="mt-1 block px-3 py-1.5 text-puntt-neutral-gray-10 transition-colors hover:text-puntt-accent-9"
              data-testid="sidebar-ask-puntt-shared-all-link"
            >
              <Text size="1">View all shared →</Text>
            </NavLink>
          </Link>
        ) : undefined
      }
    >
      {sharedChats.slice(0, SIDEBAR_SHARED_LIMIT).map((chat) => (
        <div
          key={chat._id}
          className={cx(
            "group/chat-item relative flex w-full items-center gap-4 rounded-md py-1.5 pl-3 pr-2 transition-colors hover:bg-puntt-neutral-gray-3",
            isActiveChat(chat._id) &&
              "bg-puntt-neutral-gray-3 hover:bg-puntt-neutral-gray-4",
          )}
          data-testid={`sidebar-ask-puntt-shared-chat-${chat._id}`}
          data-chat-id={chat._id}
        >
          <Link
            asChild
            size="2"
            weight="medium"
            className="puntt-link flex-1 overflow-hidden"
          >
            <NavLink
              to={askPunttChatRoute.to}
              params={{ chatId: chat._id }}
              className="w-full overflow-hidden"
            >
              <span className="block w-full truncate">{chat.title}</span>
              {chat.ownerName ? (
                <span
                  className="block w-full truncate text-puntt-neutral-gray-10"
                  style={{ fontSize: "11px", lineHeight: "14px" }}
                  data-testid="sidebar-ask-puntt-shared-chat-owner"
                >
                  owned by {chat.ownerName}
                </span>
              ) : null}
            </NavLink>
          </Link>
        </div>
      ))}
    </AskPunttSidebarSection>
  );
}
